<script>
import { some } from 'lodash-es';

export default {
  props: {
    images: {
      type: Array,
      required: true,
    },
    scalable: {
      type: Boolean,
      default: false,
    },
    buttonTitle: {
      type: String,
      default: 'Upload image',
    },
  },
  data() {
    return {
      error: false,
      duplicateError: false,
      toggled: false,
      loading: false,
      imageData: null,
      originalImageHeight: null,
      originalImageWidth: null,
      originalImageSize: null,
      imageHeight: null,
      imageWidth: null,
      imageSize: null,
      imageName: null,
      imageFile: null,
      imageUrl: null,
      imageType: null,
      dialog: false,
      fileInput: null,
      image: null,
      scale: 100,
      heightThreshold: 600,
      widthThreshold: 800,
      title: '',
    };
  },
  methods: {
    onFilePicked(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.addEventListener('load', () => {
          this.image = new Image();
          this.image.src = reader.result;
          this.image.addEventListener('load', () => {
            this.setImageInformation(
              this.image.height,
              this.image.width,
              Math.round(file.size / 1000).toFixed(1),
            );
          });
          this.scale = 100;
          this.imageName = file.name;
          this.imageUrl = reader.result;
          this.imageType = file.type;
          this.imageFile = file;
        });
      } else {
        this.clearImage();
      }
    },
    upload() {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const imageElement = document.createElement('IMG');
      imageElement.src = this.imageUrl;
      imageElement.width = this.imageWidth;
      imageElement.height = this.imageHeight;
      imageElement.addEventListener('load', () => {
        canvas.width = this.imageWidth;
        canvas.height = this.imageHeight;
        context.drawImage(imageElement, 0, 0, this.imageWidth, this.imageHeight);
        const canvasUrl = canvas.toDataURL();
        if (!this.checkForDuplicates(canvasUrl)) {
          this.error = false;
          this.duplicateError = false;
          const imageUrl = canvas.toDataURL();
          if (this.scalable) {
            this.$emit(
              'upload',
              {
                id: null,
                title: this.title,
                imageUrl,
                file: this.convertImageToFile(imageUrl),
                imageName: this.imageName,
              },
            );
          } else {
            this.$emit('upload', { id: null, title: this.title, imageUrl, file: this.imageFile });
          }
          this.close();
        } else {
          this.error = true;
          this.duplicateError = true;
        }
      });
    },
    setImageInformation(height, width, size) {
      this.originalImageHeight = height;
      this.imageHeight = height;
      this.originalImageWidth = width;
      this.imageWidth = width;
      this.originalImageSize = size;
      this.imageSize = size;
    },
    close() {
      this.toggled = false;
      this.clearImage();
    },
    clearImage() {
      this.imageName = '';
      this.imageFile = '';
      this.imageUrl = '';
      this.imageSize = null;
      this.image = null;
      this.title = '';
    },
    recalculate(value, percentage) {
      return Math.round((value / 100) * percentage);
    },
    imageHeightBelowThreshold() {
      if (this.imageHeight > this.heightThreshold) {
        return true;
      }
      return this.$t('imageUpload.errors.size', { threshold: this.heightThreshold });
    },
    imageWidthBelowThreshold() {
      if (this.imageWidth > this.widthThreshold) {
        return true;
      }
      return this.$t('imageUpload.errors.size', { threshold: this.widthThreshold });
    },
    checkForDuplicates(image) {
      return some(this.images, img => img.imageUrl === image);
    },
    convertImageToFile(image) {
      const arr = image.split(',');
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const uBarr = new Uint8Array(n);
      while (n) {
        uBarr[n] = bstr.charCodeAt(n);
        n -= 1;
      }
      return new Blob([uBarr], { type: this.imageType });
    },
  },
  watch: {
    toggled() {
      if (!this.toggled) {
        this.close();
      }
    },
    scale(percentage) {
      this.$refs.image.style.transform = `scale(${percentage / 100})`;
      this.imageHeight = this.recalculate(this.originalImageHeight, percentage);
      this.imageWidth = this.recalculate(this.originalImageWidth, percentage);
      this.imageSize = this.recalculate(this.originalImageSize, percentage);
    },
  },
  mounted() {
    this.fileInput = this.$refs.file;
  },
};
</script>

<template>
  <v-dialog
    v-model="toggled"
    :persistent="loading"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="primary"
        dark
        v-on="on"
      >
        {{ buttonTitle }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="title">
        {{ $t('imageUpload.heading') }}
        </span>
      </v-card-title>
      <v-divider/>
      <v-layout justify-center>
        <v-flex xs2>
          <div class="tw-image-upload-control">
            <div v-if="imageUrl" class="tw-image-upload-edit">
              <v-flex
                v-if="imageUrl"
                v-html="$t('imageUpload.controls.edit')"
                pa-3
                text-xs-center
              />
              <v-flex xs12 ml-2 mt-4 px-4>
                <v-slider
                  v-model="scale"
                  :disabled="!scalable"
                  :label="$t('imageUpload.controls.scale')"
                  prepend-icon="photo_size_select_large"
                  thumb-label
                  thumb-size="30"
                />
              </v-flex>
              <v-flex xs12 ml-2 pa-2>
                <v-text-field
                  v-model="scale"
                  :disabled="!scalable"
                  :label="$t('imageUpload.labels.scale')"
                  type="number"
                  suffix="%"
                />
              </v-flex>
              <v-flex xs12 ml-2 pa-2>
                <v-text-field
                  v-model="title"
                  :label="$t('imageUpload.labels.title')"
                />
              </v-flex>
              <v-flex xs12 ml-2 pa-2>
                <v-text-field
                  v-model="imageWidth"
                  :rules="[imageWidthBelowThreshold]"
                  :label="$t('imageUpload.labels.width')"
                  class="tw-image-upload-label"
                  suffix="px"
                  readonly
                  box
                />
              </v-flex>
              <v-flex xs12 ml-2 px-2>
                <v-text-field
                  v-model="imageHeight"
                  :label="$t('imageUpload.labels.height')"
                  :rules="[imageHeightBelowThreshold]"
                  class="tw-image-upload-label"
                  suffix="px"
                  readonly
                  box
                />
              </v-flex>
              <v-flex xs12 ml-2 px-2>
                <v-text-field
                  v-model="imageSize"
                  :label="$t('imageUpload.labels.size')"
                  class="tw-image-upload-label"
                  suffix="kb"
                  box
                  readonly
                />
              </v-flex>
            </div>
            <v-flex
              v-if="!imageUrl"
              v-html="$t('imageUpload.controls.noEdit')"
              pa-3
              text-xs-center
            />
          </div>
        </v-flex>
        <v-flex xs10>
          <v-layout>
            <v-flex class="xs12 text-xs-center text-sm-center text-md-center text-lg-center">
              <div class="tw-image-upload-wrapper">
                <transition-group name="fade">
                <div v-if="imageUrl"
                     :key="'tw-image-upload-image'"
                     class="tw-image-upload-image">
                  <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    @click="imageUrl = null"
                    ref="image"
                    class="tw-image-upload-file"
                  />
                </div>
                <div v-if="!imageUrl"
                     :key="'tw-image-upload-body'"
                     class="tw-image-upload-body">
                  <v-icon class="tw-image-upload-icon">cloud_upload</v-icon>
                  <span v-html="$t('imageUpload.body.upload')" />
                  <input
                    v-if="toggled"
                    @change="onFilePicked"
                    id="tw-image-upload-input"
                    class="tw-image-upload-input"
                    type="file"
                    ref="file"
                    accept="image/*"
                    placeholder="upload"
                    height="300"
                    width="auto"
                  />
                </div>
                </transition-group>
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-divider/>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="!imageUrl"
          @click="upload"
          color="primary"
          flat
        >
          {{ $t('buttons.upload') }}
        </v-btn>
        <v-btn
          @click="close"
          color="primary"
          flat
        >
          {{ $t('buttons.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-snackbar
      v-model="duplicateError"
      :timeout="4000"
      bottom>
      <v-icon class="mr-3 red--text">warning</v-icon>
      <v-flex v-if="duplicateError">
        {{ $t('imageUpload.snackbar.errors.duplicate') }}
      </v-flex>
      <v-btn
        @click="snackbar = false"
        color="white"
        flat>
        {{ $t('slopeMaps.snackbar.close') }}
      </v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<style>
  .tw-image-upload-wrapper {
    position: relative;
    width: calc(100% - 20px);
    height: 550px;
    margin: 5px;
    line-height: 600px;
    border: 3px dotted #f2f2f2;
  }

  .tw-image-upload-icon {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 550px;
  }

  .tw-image-upload-input {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 550px;
    opacity: 0;
    cursor: pointer;
  }

  .tw-image-upload-file {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 550px;
    padding: 20px;
    object-fit: contain;
    cursor: pointer;
    transition: .2s all ease;
  }

  .tw-image-upload-label input[type="text"]{
    cursor: default;
  }
</style>



// WEBPACK FOOTER //
// src/components/TwImageUpload.vue